import {
  AccountTree,
  CheckCircle,
  Close,
  Delete,
  Download,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  HourglassEmpty,
  PlayArrow,
  Summarize,
  Upload,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import CodeEditor from "components/admin/CodeEditor";
import {
  FIELD_SUMMARY_CSV,
  canMakeFieldSummary,
} from "components/admin/DemoData.support";
import DemoDataProfileViewer from "components/admin/DemoDataProfileViewer";
import DemoRunProgress from "components/admin/DemoRunProgress";
import CommonButton from "components/common/Button";
import CopyButton from "components/common/CopyButton";
import TextField from "components/common/TextField";
import DashboardContent from "components/layout/DashboardContent";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  useCreateDemoRunFieldSummaryMutation,
  useGetDemoRunLoadedQuery,
  useGetDemoRunLogQuery,
  useGetDemoRunProfileQuery,
  useGetDemoRunStatusQuery,
  useLazyGetDemoRunDownloadQuery,
  useListCompaniesQuery,
  useListDemoRunsQuery,
  useStopDemoRunMutation,
  useUploadDemoProfileMutation,
  useDeleteDemoRunMutation,
  useTriggerDemoGenerateMutation,
  useTriggerDemoProfileMutation,
} from "services/adminService";

const CONFIRM_PHRASE = "continue with demo data";

// Suggestions only — both pickers accept any free-text model name
const OLLAMA_MODEL_OPTIONS = [
  "qwen3:30b-a3b-instruct-2507-q4_K_M",
  "qwen3:32b",
  "qwen3:14b",
  "qwen3:8b",
  "gemma3:27b",
  "llama3.1:8b",
  "phi4:14b",
];
const ANTHROPIC_MODEL_OPTIONS = [
  "claude-sonnet-5-5",
  "claude-sonnet-5",
  "claude-opus-5-5",
  "claude-opus-5",
  "claude-sonnet-4-6",
  "claude-haiku-4-5",
  "claude-opus-4-8",
];
// GPU types for local models; CPU types suffice for anthropic-provider runs
const INSTANCE_TYPE_OPTIONS = [
  "g5.xlarge",
  "g5.2xlarge",
  "g5.4xlarge",
  "g5.12xlarge",
  "g6.xlarge",
  "g6e.xlarge",
  "m6i.xlarge",
  "c6i.2xlarge",
];
const DEFAULT_INSTANCE_TYPE = "g5.xlarge";
const DEFAULT_MAX_WORK_MINS = 120;

function ModelPicker({ label, value, onChange, options }) {
  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value}
      onInputChange={(_, v) => onChange(v)}
      renderInput={(params) => (
        <MuiTextField {...params} label={label} variant={"filled"} fullWidth />
      )}
    />
  );
}

const STEP_LABELS = {
  starting: "1 · Starting",
  setup: "2 · Setup",
  model: "3 · Loading model",
  running: "4 · Running",
  done: "Done",
  stopped: "Stopped",
  failed: "Failed",
};

export function RunStatusChip({ step }) {
  if (step === "done") {
    return (
      <Chip
        icon={<CheckCircle />}
        label={"Done"}
        color={"success"}
        size={"small"}
        variant={"outlined"}
      />
    );
  }
  if (step === "stopped") {
    return (
      <Chip
        label={"Stopped"}
        color={"default"}
        size={"small"}
        variant={"outlined"}
      />
    );
  }
  if (step === "failed") {
    return (
      <Chip
        icon={<ErrorOutline />}
        label={"Failed"}
        color={"error"}
        size={"small"}
        variant={"outlined"}
      />
    );
  }
  return (
    <Chip
      icon={<HourglassEmpty />}
      label={STEP_LABELS[step] || "Running"}
      color={"warning"}
      size={"small"}
      variant={"outlined"}
    />
  );
}

function parseRunId(runId) {
  const ts = runId.replace(/_gen$/, "");
  if (ts.length < 15) return null;
  try {
    return new Date(
      `${ts.slice(0, 4)}-${ts.slice(4, 6)}-${ts.slice(6, 8)}T${ts.slice(
        9,
        11
      )}:${ts.slice(11, 13)}:${ts.slice(13, 15)}Z`
    );
  } catch {
    return null;
  }
}

function formatRunTime(runId) {
  const d = parseRunId(runId);
  if (!d) return runId;
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  // Past 2 days, "ago" stops being useful — show the absolute date instead
  if (mins >= 2 * 24 * 60) {
    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const days = Math.floor(mins / (24 * 60));
  const hours = Math.floor((mins % (24 * 60)) / 60);
  const rem = mins % 60;
  const el =
    mins < 1
      ? "just now"
      : mins < 60
      ? `${mins}m ago`
      : days < 1
      ? `${hours}h ${rem}m ago`
      : `${days}d ${hours}h ago`;
  return `${d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })} · ${el}`;
}

function formatDuration(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ${seconds % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function RunRow({ run, companyId, onGenerateFrom, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [showLoaded, setShowLoaded] = useState(false);
  const [showExplorer, setShowExplorer] = useState(false);
  const [stopDemoRun, { isLoading: stopping }] = useStopDemoRunMutation();
  const [
    deleteDemoRun,
    { isLoading: deleting, error: deleteError },
  ] = useDeleteDemoRunMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [
    createFieldSummary,
    { isLoading: summarising, error: summaryError },
  ] = useCreateDemoRunFieldSummaryMutation();

  const { data: statusData, refetch: refetchStatus } = useGetDemoRunStatusQuery(
    { companyId, runId: run.run_id },
    { pollingInterval: run.done ? 0 : 8000 }
  );
  // Polling stops once the list sees the run done; fetch the final status once so the row can't stay on "running"
  const wasDone = useRef(run.done);
  useEffect(() => {
    if (run.done && !wasDone.current) refetchStatus();
    wasDone.current = run.done;
  }, [run.done, refetchStatus]);

  const {
    data: profileData,
    isFetching: profileFetching,
  } = useGetDemoRunProfileQuery(
    { companyId, runId: run.run_id },
    { skip: !showProfile && !showExplorer }
  );

  const { data: logData, isFetching: logFetching } = useGetDemoRunLogQuery(
    { companyId, runId: run.run_id },
    { skip: !showLog }
  );

  const {
    data: loadedData,
    isFetching: loadedFetching,
  } = useGetDemoRunLoadedQuery(
    { companyId, runId: run.run_id },
    { skip: !showLoaded }
  );

  const [
    getDownload,
    { error: downloadError },
  ] = useLazyGetDemoRunDownloadQuery();

  const status = statusData || run;
  const step =
    status.step ||
    (status.failed ? "failed" : status.done ? "done" : "starting");
  const done = status.done;
  const files = status.files || [];

  const handleDownload = async (name) => {
    const result = await getDownload({
      companyId,
      runId: run.run_id,
      file: name,
    });
    if (result.data?.url) {
      window.location.assign(result.data.url);
    }
  };

  const handleFieldSummary = async () => {
    const result = await createFieldSummary({ companyId, runId: run.run_id });
    if (result.error) return;
    // The new .md and .csv only show as file buttons once the status is read again
    refetchStatus();
    await handleDownload(FIELD_SUMMARY_CSV);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        background: "white",
      }}
    >
      <Stack direction={"row"} alignItems={"center"} spacing={1}>
        <RunStatusChip step={step} />
        <Chip
          label={run.run_type === "gen" ? "Generate" : "Profile"}
          size={"small"}
          sx={{ fontFamily: "monospace" }}
        />
        <Typography variant={"body2"} sx={{ flex: 1, color: "text.secondary" }}>
          {formatRunTime(run.run_id)}
          {status.duration_s != null
            ? ` · took ${formatDuration(status.duration_s)}`
            : ""}
        </Typography>
        {!done && (
          <CommonButton
            color={"error"}
            size={"small"}
            isLoading={stopping}
            onClick={() => stopDemoRun({ companyId, runId: run.run_id })}
            sx={{ flexShrink: 0 }}
          >
            {"Stop"}
          </CommonButton>
        )}
        <IconButton
          size={"small"}
          onClick={() => setExpanded((v) => !v)}
          sx={{ flexShrink: 0 }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>
      {(step === "running" || step === "failed") && (
        <DemoRunProgress
          progress={status.progress}
          failed={step === "failed"}
        />
      )}
      {status.meta && Object.keys(status.meta).length > 0 && (
        <Stack direction={"row"} spacing={2} sx={{ mt: 0.5, flexWrap: "wrap" }}>
          {status.meta.name && (
            <Typography variant={"caption"} sx={{ fontWeight: 600 }}>
              {status.meta.name}
            </Typography>
          )}
          {status.meta.source === "upload" && (
            <Typography variant={"caption"} color={"text.secondary"}>
              {`Uploaded by ${status.meta.uploaded_by}`}
            </Typography>
          )}
          {status.meta.note && (
            <Typography variant={"caption"} color={"text.secondary"}>
              {status.meta.note}
            </Typography>
          )}
          {status.meta.profile_limit != null && (
            <Typography variant={"caption"} color={"text.secondary"}>
              {`Limit: ${status.meta.profile_limit}`}
            </Typography>
          )}
          {status.meta.gen_count != null && (
            <Typography variant={"caption"} color={"text.secondary"}>
              {`Count: ${status.meta.gen_count}`}
            </Typography>
          )}
          {status.meta.llm_model && (
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontFamily: "monospace" }}
            >
              {status.meta.llm_model}
            </Typography>
          )}
          {status.meta.source_run_id && (
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontFamily: "monospace" }}
            >
              {status.meta.source_name
                ? `from ${status.meta.source_name} (${status.meta.source_run_id})`
                : `from ${status.meta.source_run_id}`}
            </Typography>
          )}
          {status.meta.result_prefix && (
            <Tooltip title={status.meta.result_prefix}>
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ fontFamily: "monospace" }}
              >
                {status.meta.result_prefix.replace(
                  /^s3:\/\/[^/]+\//,
                  "s3://…/"
                )}
              </Typography>
            </Tooltip>
          )}
        </Stack>
      )}
      {done && (
        <Stack
          direction={"row"}
          spacing={0.5}
          sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.5 }}
        >
          {status.has_profile && (
            <CommonButton
              size={"small"}
              color={showProfile ? "secondary" : "primary"}
              onClick={() => {
                const next = !showProfile;
                setShowProfile(next);
                if (next) setExpanded(true);
                else if (!showLog && !showLoaded) setExpanded(false);
              }}
            >
              {showProfile ? "Hide Profile" : "View Profile"}
            </CommonButton>
          )}
          {status.has_profile && (
            <CommonButton
              size={"small"}
              startIcon={<AccountTree />}
              onClick={() => setShowExplorer(true)}
            >
              {"Explore"}
            </CommonButton>
          )}
          <CommonButton
            size={"small"}
            color={showLog ? "secondary" : "primary"}
            onClick={() => {
              const next = !showLog;
              setShowLog(next);
              if (next) setExpanded(true);
              else if (!showProfile && !showLoaded) setExpanded(false);
            }}
          >
            {showLog ? "Hide Log" : "View Log"}
          </CommonButton>
          {run.run_type === "gen" && (
            <CommonButton
              size={"small"}
              color={showLoaded ? "secondary" : "primary"}
              onClick={() => {
                const next = !showLoaded;
                setShowLoaded(next);
                if (next) setExpanded(true);
                else if (!showProfile && !showLog) setExpanded(false);
              }}
            >
              {showLoaded ? "Hide Loaded" : "Loaded Submissions"}
            </CommonButton>
          )}
          {files.map((f) => (
            <CommonButton
              key={f.name}
              size={"small"}
              tooltip={`Download (${f.size} bytes)`}
              tooltipRequired
              startIcon={<Download />}
              onClick={() => handleDownload(f.name)}
              sx={{ textTransform: "none", fontFamily: "monospace" }}
            >
              {f.name}
            </CommonButton>
          ))}
          {statusData && files.length === 0 && (
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ alignSelf: "center" }}
            >
              {"No output files to download"}
            </Typography>
          )}
          {status.has_profile && (
            <CommonButton
              size={"small"}
              startIcon={<PlayArrow />}
              onClick={() => onGenerateFrom(run.run_id)}
            >
              {"Generate"}
            </CommonButton>
          )}
          {canMakeFieldSummary(run.run_type, statusData) && (
            <CommonButton
              size={"small"}
              tooltip={
                "Field overview for the client: field, what it is, format, how often it is used"
              }
              tooltipRequired
              startIcon={<Summarize />}
              isLoading={summarising}
              onClick={handleFieldSummary}
            >
              {"Client summary"}
            </CommonButton>
          )}
          <CommonButton
            size={"small"}
            color={"error"}
            startIcon={<Delete />}
            // The server cleans up after a Delete that races the summary; waiting just spares the user an error
            isDisabled={summarising}
            onClick={() => setConfirmDelete(true)}
          >
            {"Delete"}
          </CommonButton>
        </Stack>
      )}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{"Delete this run?"}</DialogTitle>
        <DialogContent>
          <Typography variant={"body2"}>
            {`All files of run ${run.run_id} are deleted from S3. This cannot be undone.`}
          </Typography>
          {deleteError && (
            <Alert severity={"error"} sx={{ mt: 1 }}>
              {String(deleteError)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <CommonButton
            color={"inherit"}
            onClick={() => setConfirmDelete(false)}
          >
            {"Cancel"}
          </CommonButton>
          <CommonButton
            color={"error"}
            isLoading={deleting}
            startIcon={<Delete />}
            onClick={async () => {
              const result = await deleteDemoRun({
                companyId,
                runId: run.run_id,
              });
              if (!result.error) {
                setConfirmDelete(false);
                onDeleted?.();
              }
            }}
          >
            {"Delete"}
          </CommonButton>
        </DialogActions>
      </Dialog>
      {summaryError && (
        <Alert severity={"error"} sx={{ mt: 0.75 }}>
          {`Client summary failed: ${String(summaryError)}`}
        </Alert>
      )}
      {downloadError && (
        <Alert severity={"error"} sx={{ mt: 0.75 }}>
          {"Download failed. The file may no longer exist."}
        </Alert>
      )}
      <Dialog
        open={showExplorer}
        onClose={() => setShowExplorer(false)}
        fullScreen
      >
        <Stack sx={{ height: "100vh" }}>
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {"Profile Explorer"}
            </Typography>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: 12,
                color: "text.secondary",
              }}
            >
              {run.run_id}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={() => setShowExplorer(false)}>
              <Close />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {profileFetching || !profileData ? (
              <Stack alignItems={"center"} sx={{ pt: 8 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : (
              <DemoDataProfileViewer profile={profileData} />
            )}
          </Box>
        </Stack>
      </Dialog>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1.5 }}>
          {showProfile && (
            <Box sx={{ mb: 1 }}>
              <Typography variant={"caption"} color={"text.secondary"}>
                {"Profile JSON"}
              </Typography>
              {profileFetching ? (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              ) : (
                <Box sx={{ mt: 0.5 }}>
                  <CodeEditor
                    value={
                      profileData
                        ? JSON.stringify(profileData, null, 2)
                        : "No data"
                    }
                    language={"json"}
                    readOnly
                    height={"50vh"}
                  />
                </Box>
              )}
            </Box>
          )}
          {showLoaded && (
            <Box sx={{ mb: 1 }}>
              <Typography variant={"caption"} color={"text.secondary"}>
                {"Loaded Submissions"}
              </Typography>
              {loadedFetching ? (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              ) : !loadedData?.submission_ids?.length ? (
                <Typography variant={"body2"} color={"text.secondary"}>
                  {"No load manifest found for this run."}
                </Typography>
              ) : (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant={"body2"} sx={{ mb: 0.5 }}>
                    {`Loaded ${loadedData.ok_count}/${loadedData.total_count}`}
                    {loadedData.api_url ? ` · ${loadedData.api_url}` : ""}
                  </Typography>
                  <Stack
                    direction={"row"}
                    spacing={0.5}
                    sx={{ flexWrap: "wrap", rowGap: 0.5 }}
                  >
                    {loadedData.submission_ids.map((sid) => (
                      <Stack key={sid} direction={"row"} alignItems={"center"}>
                        <Chip
                          label={sid}
                          size={"small"}
                          clickable
                          component={"a"}
                          href={`/submissions/${sid}`}
                          target={"_blank"}
                          sx={{ fontFamily: "monospace" }}
                        />
                        <CopyButton
                          content={sid}
                          tooltip={"Copy submission ID"}
                          scale={"small"}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
          {showLog && (
            <Box>
              <Typography variant={"caption"} color={"text.secondary"}>
                {"Pipeline Log"}
              </Typography>
              {logFetching ? (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              ) : (
                <Box sx={{ mt: 0.5 }}>
                  <CodeEditor
                    value={logData?.log || "(empty)"}
                    language={"shell"}
                    readOnly
                    height={"55vh"}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

const PROFILE_FORM_DEFAULTS = {
  profileLimit: 500,
  ollamaModel: "qwen3:30b-a3b-instruct-2507-q4_K_M",
  maxWorkMins: DEFAULT_MAX_WORK_MINS,
  instanceType: DEFAULT_INSTANCE_TYPE,
  fetchWorkers: 12,
};

function ProfileForm({ companyId, onSuccess }) {
  const [form, setForm] = useState(PROFILE_FORM_DEFAULTS);
  const [
    triggerProfile,
    { isLoading, error },
  ] = useTriggerDemoProfileMutation();
  const [uploadOpen, setUploadOpen] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async () => {
    const result = await triggerProfile({
      companyId,
      profileLimit: parseInt(form.profileLimit, 10) || 500,
      ollamaModel:
        form.ollamaModel.trim() || "qwen3:30b-a3b-instruct-2507-q4_K_M",
      maxWorkTimeS:
        (parseInt(form.maxWorkMins, 10) || DEFAULT_MAX_WORK_MINS) * 60,
      instanceType: form.instanceType.trim() || null,
      fetchWorkers: parseInt(form.fetchWorkers, 10) || 12,
    });
    if (!result.error) {
      setForm(PROFILE_FORM_DEFAULTS);
      onSuccess?.();
    }
  };

  return (
    <Stack spacing={2} useFlexGap>
      <Alert severity={"info"} sx={{ fontSize: 12 }}>
        {
          "Profiles live submissions of this company (read-only). Runs as an auto-provisioned service account derived from your login — no credentials needed."
        }
      </Alert>
      <Grid container spacing={2} alignItems={"center"}>
        <Grid item xs={6} sm={3}>
          <TextField
            label={"Profile Limit"}
            value={form.profileLimit}
            onChange={set("profileLimit")}
            type={"number"}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ModelPicker
            label={"Ollama Model"}
            value={form.ollamaModel}
            onChange={(v) => setForm((f) => ({ ...f, ollamaModel: v }))}
            options={OLLAMA_MODEL_OPTIONS}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label={"Max Work Time (min)"}
            value={form.maxWorkMins}
            onChange={set("maxWorkMins")}
            type={"number"}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ModelPicker
            label={"Instance Type"}
            value={form.instanceType}
            onChange={(v) => setForm((f) => ({ ...f, instanceType: v }))}
            options={INSTANCE_TYPE_OPTIONS}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label={"Fetch Workers"}
            value={form.fetchWorkers}
            onChange={set("fetchWorkers")}
            type={"number"}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          {error && <Alert severity={"error"}>{String(error)}</Alert>}
          <Stack direction={"row"} spacing={1}>
            <CommonButton
              onClick={handleSubmit}
              isLoading={isLoading}
              startIcon={<PlayArrow />}
              sx={{ whiteSpace: "nowrap" }}
            >
              {"Trigger Profile"}
            </CommonButton>
            <CommonButton
              onClick={() => setUploadOpen(true)}
              startIcon={<Upload />}
              sx={{ whiteSpace: "nowrap" }}
            >
              {"Upload Profile"}
            </CommonButton>
          </Stack>
          <UploadProfileDialog
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            companyId={companyId}
            onUploaded={onSuccess}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

// A profile.json downloaded in another environment becomes a finished run to generate from
function UploadProfileDialog({ open, onClose, companyId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const fileInput = useRef(null);
  const [
    uploadProfile,
    { isLoading, error, reset },
  ] = useUploadDemoProfileMutation();

  const handleClose = () => {
    setFile(null);
    setName("");
    setNote("");
    reset();
    onClose();
  };

  const handleUpload = async () => {
    const result = await uploadProfile({
      companyId,
      file,
      name: name.trim(),
      note: note.trim(),
    });
    if (!result.error) {
      onUploaded?.();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>{"Upload profile"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <input
            ref={fileInput}
            type={"file"}
            accept={".json,application/json"}
            hidden
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              e.target.value = "";
            }}
          />
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <CommonButton
              onClick={() => fileInput.current?.click()}
              startIcon={<Upload />}
              sx={{ whiteSpace: "nowrap" }}
            >
              {"Choose file"}
            </CommonButton>
            <Typography variant={"body2"} color={"text.secondary"} noWrap>
              {file ? file.name : "No profile.json chosen"}
            </Typography>
          </Stack>
          <TextField
            label={"Name"}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label={"Note"}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
          />
          {error && <Alert severity={"error"}>{String(error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <CommonButton color={"inherit"} onClick={handleClose}>
          {"Cancel"}
        </CommonButton>
        <CommonButton
          onClick={handleUpload}
          isLoading={isLoading}
          isDisabled={!file || !name.trim()}
          startIcon={<Upload />}
        >
          {"Upload"}
        </CommonButton>
      </DialogActions>
    </Dialog>
  );
}

function GenerateDialog({ open, onClose, companyId, sourceRunId, sourceName }) {
  const [form, setForm] = useState({
    genCount: 5,
    llmProvider: "anthropic",
    llmModel: "claude-sonnet-4-6",
    minPresence: "",
    labelPrefix: "",
    genSeed: "",
    renewalPct: "",
    lossRunPct: "",
    maxWorkMins: DEFAULT_MAX_WORK_MINS,
    instanceType: DEFAULT_INSTANCE_TYPE,
  });
  const [loadCompanyId, setLoadCompanyId] = useState(companyId);
  const [confirmInput, setConfirmInput] = useState("");
  const { data: companiesData } = useListCompaniesQuery();
  const companyOptions = companiesData?.map((c) => c.id)?.sort() ?? [];
  const [
    triggerGenerate,
    { isLoading, error },
  ] = useTriggerDemoGenerateMutation();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const confirmed = confirmInput.trim().toLowerCase() === CONFIRM_PHRASE;

  const handleSubmit = async () => {
    const result = await triggerGenerate({
      companyId,
      sourceRunId,
      genCount: parseInt(form.genCount, 10) || 5,
      llmProvider: form.llmProvider,
      llmModel: form.llmModel.trim(),
      minPresence: form.minPresence ? parseFloat(form.minPresence) : null,
      labelPrefix: form.labelPrefix || null,
      genSeed: form.genSeed || null,
      // Blank = the profile's renewal share
      renewalShare:
        form.renewalPct === "" ? null : parseFloat(form.renewalPct) / 100,
      // Blank = the generator's default
      lossRunShare:
        form.lossRunPct === "" ? null : parseFloat(form.lossRunPct) / 100,
      maxWorkTimeS:
        (parseInt(form.maxWorkMins, 10) || DEFAULT_MAX_WORK_MINS) * 60,
      loadCompanyId: loadCompanyId || companyId,
      instanceType: form.instanceType.trim() || null,
    });
    if (!result.error) {
      onClose(true);
    }
  };

  const handleClose = () => {
    setConfirmInput("");
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
      <DialogTitle>{"Generate Demo Submissions"}</DialogTitle>
      <DialogContent>
        {/* Flex gap, not child margins, so the Grid keeps its negative gutter margins */}
        <Stack spacing={2} sx={{ mt: 1 }} useFlexGap>
          <Typography variant={"body2"} color={"text.secondary"}>
            {"Source profile: "}
            {sourceName && <strong>{`${sourceName} `}</strong>}
            <code>{sourceRunId}</code>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <TextField
                label={"Count"}
                value={form.genCount}
                onChange={set("genCount")}
                type={"number"}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label={"Label Prefix"}
                value={form.labelPrefix}
                onChange={set("labelPrefix")}
                placeholder={"Acme Corp"}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={"Seed (optional)"}
                value={form.genSeed}
                onChange={set("genSeed")}
                type={"number"}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={"Renewals % (blank = profile)"}
                value={form.renewalPct}
                onChange={set("renewalPct")}
                type={"number"}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={"Loss Runs % (blank = 85)"}
                value={form.lossRunPct}
                onChange={set("lossRunPct")}
                type={"number"}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControl variant={"filled"} fullWidth>
                <InputLabel>{"LLM Provider"}</InputLabel>
                <Select
                  label={"LLM Provider"}
                  value={form.llmProvider}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      llmProvider: e.target.value,
                      llmModel:
                        e.target.value === "anthropic"
                          ? "claude-sonnet-4-6"
                          : "qwen3:30b-a3b-instruct-2507-q4_K_M",
                    }))
                  }
                >
                  <MenuItem value={"anthropic"}>{"Anthropic"}</MenuItem>
                  <MenuItem value={"ollama"}>{"Ollama"}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <ModelPicker
                label={"Model"}
                value={form.llmModel}
                onChange={(v) => setForm((f) => ({ ...f, llmModel: v }))}
                options={
                  form.llmProvider === "anthropic"
                    ? ANTHROPIC_MODEL_OPTIONS
                    : OLLAMA_MODEL_OPTIONS
                }
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={"Min Presence"}
                value={form.minPresence}
                onChange={set("minPresence")}
                type={"number"}
                inputProps={{ step: 0.05, min: 0, max: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={"Max Work Time (min)"}
                value={form.maxWorkMins}
                onChange={set("maxWorkMins")}
                type={"number"}
              />
            </Grid>
            <Grid item xs={6}>
              <ModelPicker
                label={"Instance Type"}
                value={form.instanceType}
                onChange={(v) => setForm((f) => ({ ...f, instanceType: v }))}
                options={INSTANCE_TYPE_OPTIONS}
              />
            </Grid>
          </Grid>
          <Divider />
          <Typography variant={"subtitle2"}>{"Target Company"}</Typography>
          <Autocomplete
            options={companyOptions}
            value={loadCompanyId}
            onChange={(_, v) => setLoadCompanyId(v ?? companyId)}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label={"Target Company"}
                variant={"filled"}
                helperText={
                  "Where generated submissions will be loaded (this environment). Runs as an auto-provisioned service account — no credentials needed."
                }
              />
            )}
          />
          <Divider />
          <Alert severity={"warning"}>
            {
              "This will create synthetic submissions in the target environment. Type "
            }
            <strong>{CONFIRM_PHRASE}</strong>
            {" to confirm."}
          </Alert>
          <TextField
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={CONFIRM_PHRASE}
          />
          {error && <Alert severity={"error"}>{String(error)}</Alert>}
        </Stack>
      </DialogContent>
      {/* Same side padding as DialogContent so the buttons line up with the fields */}
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CommonButton color={"inherit"} onClick={handleClose}>
          {"Cancel"}
        </CommonButton>
        <CommonButton
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={!confirmed}
          startIcon={<PlayArrow />}
        >
          {"Start Generation"}
        </CommonButton>
      </DialogActions>
    </Dialog>
  );
}

export default function DemoData() {
  const { companyId } = useParams();
  const [tab, setTab] = useState(0);
  const [genTab, setGenTab] = useState(0);
  const [generateSourceRunId, setGenerateSourceRunId] = useState(null);

  const { data: runsData, isFetching, refetch } = useListDemoRunsQuery(
    { companyId },
    { pollingInterval: 15000 }
  );

  const runs = runsData?.runs || [];
  const profileTypeRuns = runs.filter((r) => r.run_type !== "gen");
  const genRuns = runs.filter((r) => r.run_type === "gen");
  const profileRuns = runs.filter((r) => r.has_profile && r.done);

  const handleGenerateFrom = (runId) => {
    setGenerateSourceRunId(runId);
  };

  const handleGenerateClose = (triggered) => {
    setGenerateSourceRunId(null);
    if (triggered) refetch();
  };

  return (
    <AdminDashboard>
      <DashboardContent isFullWidth={false}>
        <Component
          title={"Demo Data"}
          isFetching={isFetching}
          options={
            <CommonButton
              size={"small"}
              onClick={refetch}
              isDisabled={isFetching}
            >
              {"Refresh"}
            </CommonButton>
          }
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label={"Profile Runs"} />
            <Tab label={"Generate"} />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={3}>
              <ProfileForm companyId={companyId} onSuccess={refetch} />
              <Divider />
              <Typography variant={"subtitle2"} color={"text.secondary"}>
                {`Profile Runs (${profileTypeRuns.length})`}
              </Typography>
              {profileTypeRuns.length === 0 && !isFetching ? (
                <Typography variant={"body2"} color={"text.secondary"}>
                  {"No runs yet. Trigger a profile run above to get started."}
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {profileTypeRuns.map((run) => (
                    <RunRow
                      key={run.run_id}
                      run={run}
                      companyId={companyId}
                      onGenerateFrom={handleGenerateFrom}
                      onDeleted={refetch}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={2}>
              <Tabs
                value={genTab}
                onChange={(_, v) => setGenTab(v)}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label={`Generation (${genRuns.length})`} />
                <Tab label={`Profile (${profileRuns.length})`} />
              </Tabs>

              {genTab === 0 && (
                <>
                  <Alert severity={"info"}>
                    {"To start a new generation, open the "}
                    <strong>{"Profile"}</strong>
                    {" tab and click "}
                    <strong>{"Generate"}</strong>
                    {" on a completed profile run."}
                  </Alert>
                  {genRuns.length === 0 ? (
                    <Typography variant={"body2"} color={"text.secondary"}>
                      {"No generation runs yet."}
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {genRuns.map((run) => (
                        <RunRow
                          key={run.run_id}
                          run={run}
                          companyId={companyId}
                          onGenerateFrom={handleGenerateFrom}
                          onDeleted={refetch}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}

              {genTab === 1 && (
                <>
                  <Alert severity={"info"}>
                    {"Click "}
                    <strong>{"Generate"}</strong>
                    {" on any completed profile run below to start generation."}
                  </Alert>
                  {profileRuns.length === 0 ? (
                    <Typography variant={"body2"} color={"text.secondary"}>
                      {
                        "No completed profile runs available. Go to Profile Runs to create one."
                      }
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {profileRuns.map((run) => (
                        <RunRow
                          key={run.run_id}
                          run={run}
                          companyId={companyId}
                          onGenerateFrom={handleGenerateFrom}
                          onDeleted={refetch}
                        />
                      ))}
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}
        </Component>
      </DashboardContent>

      <GenerateDialog
        open={!!generateSourceRunId}
        onClose={handleGenerateClose}
        companyId={companyId}
        sourceRunId={generateSourceRunId}
        sourceName={
          runs.find((r) => r.run_id === generateSourceRunId)?.meta?.name
        }
      />
    </AdminDashboard>
  );
}
